import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { applyDefaultConsent } from "@/lib/consent";

// Cross-origin "Script error." messages (Calendly widget, Clarity, etc.)
// are tainted by the browser — the React-Scripts dev-server overlay traps
// them and renders a full-screen modal even though the actual page renders
// fine. Production builds don't show this overlay. Filtering them here
// keeps the dev experience clean without hiding real errors (real errors
// always carry a non-empty filename/lineno).
//
// Also silences known-noise errors from Emergent's own visual-edits package
// which references React components by their LOWERCASED name as globals
// (e.g. "can't find variable: landingtabs" when a component is named
// LandingTabs). These fire ONLY when the Emergent editor overlay loads,
// don't affect real caregivers, but were inflating Microsoft Clarity's
// error count to ~4% of sessions. Keep this list tight — only truly noisy,
// known-benign patterns.
const VISUAL_EDITS_NOISE = [
    "landingtabs",
    "rotatingquotecard",
    "animatedhero",
];
function isVisualEditsNoise(msg) {
    if (!msg) return false;
    const lower = String(msg).toLowerCase();
    return VISUAL_EDITS_NOISE.some(name => lower.includes(name));
}

window.addEventListener("error", (e) => {
    if (e && e.message === "Script error." && !e.filename) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return;
    }
    if (e && isVisualEditsNoise(e.message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
    }
}, true);
window.addEventListener("unhandledrejection", (e) => {
    const msg = (e && e.reason && (e.reason.message || String(e.reason))) || "";
    if (msg === "Script error." || msg.includes("calendly")) {
        e.preventDefault();
        return;
    }
    if (isVisualEditsNoise(msg)) {
        e.preventDefault();
    }
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60_000,
            refetchOnWindowFocus: false,
        },
    },
});

// Tell GA4 (Consent Mode v2) what's allowed BEFORE gtag.js has a chance to
// drop cookies, and conditionally inject Microsoft Clarity. Clarity used
// to load unconditionally here — now it only loads after the caregiver
// opts in (or returns with an existing opt-in in localStorage).
applyDefaultConsent();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <App />
        </QueryClientProvider>
    </React.StrictMode>,
);
